import React, { useMemo } from 'react';
import { Card, CardFooter } from '@frontend/shadcn/components/ui/card';
import { PostInsights } from '@frontend/gql/social/graphql';
import { PostCard, PostCardMedia, PostCardSkeleton } from '@frontend/applications/Social/components/post-card';
import { useTabNavigation } from '../../hooks/useTabNavigation';
import { GraphCardHeader, GraphCardHeaderRow, GraphCardTitle, GraphCardContent } from '../../../components/graph-card';
import TopPostCard from './top-post-card';

interface TopPerformersRowProps {
  topPosts?: PostInsights[] | null;
  topPostsFetching?: boolean;
}

export const TopPerformersRow: React.FC<TopPerformersRowProps> = ({
  topPosts,
  topPostsFetching = false
}) => {
  const { handleTabChange } = useTabNavigation();

  const content = useMemo(() => {
    if (topPostsFetching) {
      return Array.from({ length: 10 }).map((_, idx) => (
        <div
          key={idx}
          className="w-[280px] flex-shrink-0"
          role="listitem"
        >
          <PostCard>
            <PostCardMedia>
              <PostCardSkeleton />
            </PostCardMedia>
          </PostCard>
        </div>
      ));
    }

    if (!topPosts || topPosts.length === 0) {
      return (
        <div className="col-span-full text-center text-muted-foreground py-8">
          No top posts available.
        </div>
      );
    }

    return topPosts.map((post) => (
      <div
        key={`${post.entityId}-${post.entityNetwork}`}
        className="w-[280px] flex-shrink-0"
        role="listitem"
      >
        <TopPostCard
          post={post}
        />
      </div>
    ));
  }, [topPostsFetching, topPosts]);

  return (
    <div className="space-y-6" role="region" aria-label="Top performers">
      {/* Header */}
      <h2 className="font-semibold text-gray-900" style={{ fontSize: '20px' }}>Top Performers</h2>

      <Card className="overflow-visible">
        <GraphCardHeader className="border-b">
          <GraphCardHeaderRow>
            <GraphCardTitle>Top Posts</GraphCardTitle>
          </GraphCardHeaderRow>
        </GraphCardHeader>
        <GraphCardContent className="pt-4">
          <div
            className="flex overflow-x-auto gap-4 pb-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
            role="list"
            aria-label="Top performers list"
          >
            {content}
          </div>
        </GraphCardContent>
        <CardFooter className="pt-2">
          <button
            className="text-primary-600 font-medium hover:underline text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:no-underline"
            onClick={() => handleTabChange('posts')}
            disabled={topPostsFetching || !topPosts || topPosts.length === 0}
            aria-disabled={topPostsFetching || !topPosts || topPosts.length === 0}
          >
            View More
          </button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default TopPerformersRow;
